// Pago de Clientes › Tesorería › Cobros en ruta (pedido de Rodrigo, 2026-11-01). El efectivo que los
// vendedores recibieron en la calle (comprobantes CR-…): tesorería cuenta lo que el vendedor entrega y lo
// INGRESA (nace el recibo RC-… que aplica el pago a los documentos y ahí se formaliza) o ANULA el
// comprobante mal hecho. Con el filtro de estado, vendedor y fechas es también la auditoría del vendedor
// cobrador: qué recibió, cuándo, quién lo ingresó, con qué recibo y lo que lleva días sin entregar.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, guardarArchivo } from '../api/cliente';
import { useVendedores } from '../api/consultas';
import { PanelError } from '../comun/Estados';
import { pesos } from '../comun/formato';
import { Modal } from '../comun/Modal';
import { useSesion } from '../sesion/SesionContexto';
import {
  archivoComprobante,
  dibujarComprobante,
  fechaHoraChile,
  nombreDocumento,
  type VistaRecepcion,
} from './comprobanteRuta';

type Estado = 'PENDIENTE' | 'INGRESADA' | 'ANULADA' | '';
const NOMBRE_ESTADO: Record<VistaRecepcion['estado'], string> = {
  PENDIENTE: 'Por ingresar',
  INGRESADA: 'Ingresado',
  ANULADA: 'Anulado',
};
const TONO: Record<VistaRecepcion['estado'], string> = {
  PENDIENTE: 'tono-aviso',
  INGRESADA: 'tono-ok',
  ANULADA: 'tono-malo',
};

const hoy = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago' }).format(new Date());
const haceDias = (n: number) => {
  const d = new Date(`${hoy()}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};
const diasDesde = (iso: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));

interface ResultadoIngreso {
  ingresadas: Array<{
    recepcionId: string;
    reciboId: string;
    numeroRecibo: string;
    aplicado: string;
    aFavor: string;
  }>;
  errores: Array<{ recepcionId: string; codigo: string; mensaje: string }>;
}

export function CobrosRuta() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const vendedores = useVendedores(empresaId);
  const [estado, setEstado] = useState<Estado>('PENDIENTE');
  const [vendedorId, setVendedorId] = useState('');
  const [desde, setDesde] = useState(haceDias(30));
  const [hasta, setHasta] = useState(hoy());
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [confirmar, setConfirmar] = useState(false);
  const [ingresando, setIngresando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoIngreso | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const [anular, setAnular] = useState<VistaRecepcion | null>(null);
  const [ver, setVer] = useState<VistaRecepcion | null>(null);
  const puedeIngresar = puede('tesoreria.recepcion.registrar');

  // Lo pendiente se ve completo (lleve los días que lleve); lo resuelto, por fechas.
  const q = useQuery({
    queryKey: ['tesoreria', 'cobros-ruta', empresaId, estado, vendedorId, desde, hasta],
    queryFn: () =>
      api.get<VistaRecepcion[]>('/tesoreria/cobros-ruta', {
        query: {
          empresaId,
          ...(estado ? { estado } : {}),
          ...(vendedorId ? { vendedorId } : {}),
          ...(estado !== 'PENDIENTE' ? { desde, hasta } : {}),
        },
      }),
  });
  const filas = q.data ?? [];
  useEffect(() => setMarcadas(new Set()), [estado, vendedorId]);

  const porVendedor = useMemo(() => {
    const m = new Map<
      string,
      { codigo: string; nombre: string; n: number; total: number; viejo: string }
    >();
    for (const x of filas.filter((f) => f.estado === 'PENDIENTE')) {
      const v = m.get(x.vendedor.id) ?? {
        codigo: x.vendedor.codigo,
        nombre: x.vendedor.nombre,
        n: 0,
        total: 0,
        viejo: x.recibidoEn,
      };
      v.n++;
      v.total += Number(x.total);
      if (x.recibidoEn < v.viejo) v.viejo = x.recibidoEn;
      m.set(x.vendedor.id, v);
    }
    return [...m.entries()].sort((a, b) => a[1].codigo.localeCompare(b[1].codigo));
  }, [filas]);
  const elegidas = filas.filter((x) => marcadas.has(x.recepcionId));
  const totalElegido = elegidas.reduce((t, x) => t + Number(x.total), 0);
  const pendientes = filas.filter((x) => x.estado === 'PENDIENTE');

  const refrescar = () => void qc.invalidateQueries({ queryKey: ['tesoreria', 'cobros-ruta'] });
  const ingresar = async () => {
    setIngresando(true);
    setErrorAccion(null);
    try {
      const r = await api.post<ResultadoIngreso>('/tesoreria/cobros-ruta/ingresar', {
        empresaId,
        recepcionIds: elegidas.map((x) => x.recepcionId),
      });
      setResultado(r);
      setConfirmar(false);
      setMarcadas(new Set());
      refrescar();
    } catch (e) {
      setErrorAccion(e instanceof Error ? e.message : String(e));
    } finally {
      setIngresando(false);
    }
  };

  return (
    <div className="pantalla consulta cobros-ruta" data-pantalla="cobros-ruta">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado"
            value={estado}
            onChange={(e) => setEstado(e.target.value as Estado)}
          >
            <option value="PENDIENTE">Por ingresar</option>
            <option value="INGRESADA">Ingresados</option>
            <option value="ANULADA">Anulados</option>
            <option value="">Todos</option>
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Vendedor</span>
          <select
            aria-label="Vendedor"
            value={vendedorId}
            onChange={(e) => setVendedorId(e.target.value)}
          >
            <option value="">Todos los vendedores</option>
            {(vendedores.data ?? [])
              .filter((v) => v.activo)
              .sort((a, b) => a.codigo.localeCompare(b.codigo))
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.codigo} · {v.nombre}
                </option>
              ))}
          </select>
        </label>
        {estado !== 'PENDIENTE' && (
          <>
            <label className="campo">
              <span className="etiqueta">Desde</span>
              <input
                type="date"
                aria-label="Desde"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </label>
            <label className="campo">
              <span className="etiqueta">Hasta</span>
              <input
                type="date"
                aria-label="Hasta"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
            </label>
          </>
        )}
        <span className="espaciador" />
        {puedeIngresar && (
          <button
            type="button"
            className="primario"
            disabled={!elegidas.length}
            onClick={() => setConfirmar(true)}
          >
            Ingresar{' '}
            {elegidas.length
              ? `${elegidas.length} · ${pesos(String(totalElegido))}`
              : 'seleccionados'}
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}

      {porVendedor.length > 0 && (
        <fieldset className="recuadro-ficha">
          <legend>Por entregar en tesorería</legend>
          <table className="grilla compacta" aria-label="Por entregar por vendedor">
            <thead>
              <tr>
                <th>Vendedor</th>
                <th className="al-der">Comprobantes</th>
                <th className="al-der">Total en efectivo</th>
                <th className="al-der">El más antiguo</th>
              </tr>
            </thead>
            <tbody>
              {porVendedor.map(([id, v]) => (
                <tr key={id}>
                  <td>
                    {v.codigo} · {v.nombre}
                  </td>
                  <td className="al-der">{v.n}</td>
                  <td className="al-der">
                    <b>{pesos(String(v.total))}</b>
                  </td>
                  <td className={`al-der ${diasDesde(v.viejo) >= 1 ? 'tono-malo-texto' : ''}`}>
                    {diasDesde(v.viejo) === 0 ? 'hoy' : `hace ${diasDesde(v.viejo)} día(s)`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
      )}

      <table className="grilla compacta" aria-label="Cobros en ruta">
        <thead>
          <tr>
            <th style={{ width: '2rem' }}>
              {puedeIngresar && pendientes.length > 0 && (
                <input
                  type="checkbox"
                  aria-label="Marcar todos los por ingresar"
                  checked={
                    pendientes.length > 0 && pendientes.every((x) => marcadas.has(x.recepcionId))
                  }
                  onChange={(e) =>
                    setMarcadas(
                      new Set(e.target.checked ? pendientes.map((x) => x.recepcionId) : []),
                    )
                  }
                />
              )}
            </th>
            <th>Comprobante</th>
            <th>Recibido</th>
            <th>Vendedor</th>
            <th>Cliente</th>
            <th>Documentos</th>
            <th className="al-der">Efectivo</th>
            <th>Estado</th>
            <th>Ingreso / anulación</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {q.isLoading ? (
            <tr>
              <td colSpan={10}>Cargando…</td>
            </tr>
          ) : !filas.length ? (
            <tr>
              <td colSpan={10}>
                {estado === 'PENDIENTE'
                  ? 'No hay cobros en ruta por ingresar.'
                  : 'Sin cobros en ruta en esas fechas.'}
              </td>
            </tr>
          ) : (
            filas.map((x) => (
              <tr key={x.recepcionId}>
                <td>
                  {puedeIngresar && x.estado === 'PENDIENTE' && (
                    <input
                      type="checkbox"
                      aria-label={`Marcar ${x.numero}`}
                      checked={marcadas.has(x.recepcionId)}
                      onChange={(e) =>
                        setMarcadas((m) => {
                          const n = new Set(m);
                          if (e.target.checked) n.add(x.recepcionId);
                          else n.delete(x.recepcionId);
                          return n;
                        })
                      }
                    />
                  )}
                </td>
                <td className="mono">{x.numero}</td>
                <td>
                  {fechaHoraChile(x.recibidoEn)}
                  <div className="texto-suave">por {x.recibidoPor.nombre}</div>
                </td>
                <td>
                  {x.vendedor.codigo} · {x.vendedor.nombre}
                </td>
                <td>
                  {x.cliente.razonSocial}
                  {x.cliente.rut && <div className="texto-suave mono">{x.cliente.rut}</div>}
                </td>
                <td>
                  {x.documentos.map((d) => (
                    <div key={d.documentoId}>
                      {nombreDocumento(d)} · {pesos(d.monto)}
                    </div>
                  ))}
                  {x.observacion && <div className="texto-suave">Nota: {x.observacion}</div>}
                </td>
                <td className="al-der">
                  <b>{pesos(x.total)}</b>
                </td>
                <td>
                  <span className={`insignia ${TONO[x.estado]}`}>{NOMBRE_ESTADO[x.estado]}</span>
                  {x.estado === 'PENDIENTE' && diasDesde(x.recibidoEn) >= 1 && (
                    <div className="tono-malo-texto">hace {diasDesde(x.recibidoEn)} día(s)</div>
                  )}
                </td>
                <td>
                  {x.resolucion ? (
                    <>
                      {fechaHoraChile(x.resolucion.resueltoEn)} · {x.resolucion.resueltoPor.nombre}
                      {x.resolucion.recibo && (
                        <div className="mono">Recibo {x.resolucion.recibo.numero}</div>
                      )}
                      {x.resolucion.detalle && (
                        <div className="texto-suave">{x.resolucion.detalle}</div>
                      )}
                    </>
                  ) : (
                    ''
                  )}
                </td>
                <td className="acciones-fila">
                  <button type="button" onClick={() => setVer(x)}>
                    Comprobante
                  </button>
                  {puedeIngresar && x.estado === 'PENDIENTE' && (
                    <button type="button" onClick={() => setAnular(x)}>
                      Anular
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {confirmar && (
        <Modal
          titulo="Ingresar a tesorería"
          alCerrar={() => setConfirmar(false)}
          pie={
            <>
              <button type="button" onClick={() => setConfirmar(false)} disabled={ingresando}>
                Volver
              </button>
              <button
                type="button"
                className="primario"
                data-autofoco
                disabled={ingresando}
                onClick={() => void ingresar()}
              >
                {ingresando ? 'Ingresando…' : `Recibí ${pesos(String(totalElegido))} en efectivo`}
              </button>
            </>
          }
        >
          <p>
            Cuenta el efectivo que te entrega el vendedor. Al confirmar, cada comprobante pasa a un
            recibo de caja y el pago queda aplicado a sus documentos.
          </p>
          <ul>
            {elegidas.map((x) => (
              <li key={x.recepcionId}>
                {x.numero} · {x.vendedor.codigo} · {x.cliente.razonSocial} · <b>{pesos(x.total)}</b>
              </li>
            ))}
          </ul>
          <p>
            Total: <b>{pesos(String(totalElegido))}</b>
          </p>
          {errorAccion && <div className="mensaje-error">{errorAccion}</div>}
        </Modal>
      )}

      {resultado && (
        <Modal titulo="Resultado del ingreso" alCerrar={() => setResultado(null)}>
          {resultado.ingresadas.length > 0 && (
            <>
              <p>Ingresados a tesorería:</p>
              <ul>
                {resultado.ingresadas.map((x) => {
                  const r = filas.find((f) => f.recepcionId === x.recepcionId);
                  return (
                    <li key={x.recepcionId}>
                      {r?.numero ?? ''} → recibo <b className="mono">{x.numeroRecibo}</b> · aplicado{' '}
                      {pesos(x.aplicado)}
                      {Number(x.aFavor) > 0 && <> · queda a favor del cliente {pesos(x.aFavor)}</>}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {resultado.errores.length > 0 && (
            <div className="mensaje-error">
              No se pudieron ingresar:
              <ul>
                {resultado.errores.map((x) => {
                  const r = filas.find((f) => f.recepcionId === x.recepcionId);
                  return (
                    <li key={x.recepcionId}>
                      {r?.numero ?? x.recepcionId}: {x.mensaje}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Modal>
      )}

      {anular && (
        <AnularComprobante
          empresaId={empresaId}
          x={anular}
          alCerrar={() => setAnular(null)}
          alAnular={() => {
            setAnular(null);
            refrescar();
          }}
        />
      )}
      {ver && <VerComprobante x={ver} alCerrar={() => setVer(null)} />}
    </div>
  );
}

function AnularComprobante({
  empresaId,
  x,
  alCerrar,
  alAnular,
}: {
  empresaId: string;
  x: VistaRecepcion;
  alCerrar: () => void;
  alAnular: () => void;
}) {
  const [detalle, setDetalle] = useState('');
  const [grabando, setGrabando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmar = async () => {
    setGrabando(true);
    setError(null);
    try {
      await api.post(`/tesoreria/cobros-ruta/${x.recepcionId}/anular`, {
        empresaId,
        detalle: detalle.trim() || null,
      });
      alAnular();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGrabando(false);
    }
  };
  return (
    <Modal
      titulo={`Anular ${x.numero}`}
      ancho="angosto"
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={grabando}>
            Volver
          </button>
          <button
            type="button"
            className="peligro"
            disabled={grabando}
            onClick={() => void confirmar()}
          >
            {grabando ? 'Anulando…' : 'Anular comprobante'}
          </button>
        </>
      }
    >
      <p>
        {x.cliente.razonSocial} · {pesos(x.total)} recibido por {x.vendedor.codigo} el{' '}
        {fechaHoraChile(x.recibidoEn)}. El pago deja de estar en curso y el comprobante no vale.
      </p>
      <label className="campo">
        <span className="etiqueta">Detalle (opcional)</span>
        <input
          aria-label="Detalle de la anulación"
          value={detalle}
          maxLength={300}
          onChange={(e) => setDetalle(e.target.value)}
        />
      </label>
      {error && <div className="mensaje-error">{error}</div>}
    </Modal>
  );
}

function VerComprobante({ x, alCerrar }: { x: VistaRecepcion; alCerrar: () => void }) {
  const [png, setPng] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    let u: string | null = null;
    dibujarComprobante(x)
      .then((b) => {
        if (!vivo) return;
        setPng(b);
        u = URL.createObjectURL(b);
        setUrl(u);
      })
      .catch((e: unknown) => vivo && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      vivo = false;
      if (u) URL.revokeObjectURL(u);
    };
  }, [x]);
  return (
    <Modal
      titulo={`Comprobante ${x.numero}`}
      alCerrar={alCerrar}
      pie={
        <button
          type="button"
          disabled={!png}
          onClick={() => png && guardarArchivo(png, archivoComprobante(x))}
        >
          Descargar
        </button>
      }
    >
      {error ? (
        <div className="mensaje-error">{error}</div>
      ) : url ? (
        <img
          src={url}
          alt={`Comprobante ${x.numero}`}
          style={{ display: 'block', width: '100%', maxWidth: 480, margin: '0 auto' }}
        />
      ) : (
        <p>Armando el comprobante…</p>
      )}
    </Modal>
  );
}
